import { useLayoutEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { Skeleton } from '@/components/ui/loader'
import { releasePrerender } from '@/lib/prerender'
import { PageBeaconBoundary } from '@/components/marketing/PageBeaconBoundary'
import { usePublicThemeColor } from '@/hooks/usePublicThemeColor'

/**
 * `/` for en innlogget bruker er ikke en salgsside.
 *
 * ## Hva David så
 *
 * En innloggingslenke i en e-post landet på landingssiden — «Arrangementer som
 * faktisk blir noe av», priser, argumenter for hvorfor man bør bruke produktet.
 * Til en person som nettopp ba om å få logge INN. Han har allerede kjøpt; det
 * han vil er å komme i gang med noe.
 *
 * ## Hvorfor det skjedde her, og ikke i lenka
 *
 * Appens egne auth-kall sender riktig `emailRedirectTo` — `/dashboard` for
 * innlogging, `/reset-password` for passord, `/auth/confirm-email` for
 * bekreftelse. Men enhver lenke UTEN en eksplisitt `redirect_to` faller tilbake
 * på prosjektets `SITE_URL`, som er roten. Det gjelder alt som genereres uten
 * å huske parameteren: `admin/generate_link` fra et skript, en gammel lenke i
 * en innboks, en integrasjon skrevet senere.
 *
 * Å sette parameteren på hvert kallsted er å rette det på N steder og glemme
 * det på N+1. Roten er at `/` viste markedsføring til noen som er logget inn.
 * Nå gjør den ikke det, og da spiller det ingen rolle hvor lenka pekte.
 *
 * ## Hvorfor ikke bare bytte SITE_URL
 *
 * `SITE_URL` er også fallbacken for utlogget-tilfeller og for alt annet GoTrue
 * gjør. Å peke den på `/dashboard` ville sendt folk uten sesjon inn i en
 * beskyttet rute og videre til `/login` — en omvei, og en dårligere feilmelding
 * enn landingssiden er.
 */
export function LandingOrDashboard({ landing }: { landing: React.ReactNode }) {
  const { session, isLoading } = useAuthStore()
  // The prerendered landing is on screen while the session is read; a signed-in visitor gets the app's colour back.
  usePublicThemeColor(!session)

  // While the session is being read, the build-time landing (index.html) stays on screen instead of
  // this skeleton; a visitor who turns out to be signed in leaves it for the dashboard here.
  useLayoutEffect(() => {
    if (!isLoading && session) releasePrerender()
  }, [isLoading, session])

  // Sesjonen avgjøres asynkront. Å vise landingssiden imens ville gitt et blink
  // av salgsargumenter før dashbordet — verre enn å vente et øyeblikk.
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-[var(--surface-page)]">
        <div className="w-64 space-y-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
    )
  }

  if (session) return <><PageBeaconBoundary /><Navigate to="/dashboard" replace /></>
  return <><PageBeaconBoundary />{landing}</>
}
